import { useCallback, useEffect, useRef, useState, } from 'react';
import { generateEmpty, isDigitsKey, isValid, sanitizeDigits } from '../utils/codeInput';
/**
 * Coerces an externally controlled value into the internal representation:
 * exactly `length` characters, digits kept in place and everything else
 * replaced by a space so each slot maps to one input position.
 */
const normalizeControlledValue = (value, length) => value
    .split('')
    .slice(0, length)
    .map((character) => (/[0-9]/.test(character) ? character : ' '))
    .join('')
    .padEnd(length, ' ');
export function useCodeInput({ length, onComplete, resetOnComplete, value, onChange }) {
    const isControlled = value !== undefined;
    const [internalCode, setInternalCode] = useState(generateEmpty(length));
    const code = isControlled ? normalizeControlledValue(value, length) : internalCode;
    const inputsRef = useRef([]);
    const onCompleteRef = useRef(onComplete);
    const onChangeRef = useRef(onChange);
    const valueRef = useRef(value);
    valueRef.current = value;
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    const setCode = useCallback((action) => {
        // Controlled: derive the next value from the parent-owned value and report it; never touch internal state.
        if (valueRef.current !== undefined) {
            const current = normalizeControlledValue(valueRef.current, length);
            const next = typeof action === 'function' ? action(current) : action;
            onChangeRef.current?.(next);
            return;
        }
        // Uncontrolled: forward the action so React's functional updates accumulate batched keystrokes correctly.
        setInternalCode(action);
    }, [length]);
    useEffect(() => {
        if (isValid(code, length)) {
            onCompleteRef.current(code);
            if (resetOnComplete) {
                setTimeout(() => setCode(generateEmpty(length)), 200);
            }
        }
    }, [code, length, resetOnComplete, setCode]);
    const setFocus = useCallback((position) => {
        if (position >= length) {
            inputsRef.current[0]?.focus();
            inputsRef.current[0]?.blur();
        }
        else {
            inputsRef.current[position]?.focus();
        }
    }, [length]);
    const handleCodeChange = useCallback((position, keyPressed) => {
        const isDigit = isDigitsKey(keyPressed);
        const isBackspace = keyPressed === 'Backspace';
        const isDelete = keyPressed === 'Delete';
        if (keyPressed === 'ArrowLeft')
            setFocus(position - 1);
        if (keyPressed === 'ArrowRight')
            setFocus(position + 1);
        if (keyPressed === 'Home')
            setFocus(0);
        if (keyPressed === 'End')
            setFocus(length - 1);
        if (!isDigit && !isBackspace && !isDelete)
            return;
        setCode((prev) => {
            const chars = prev.split('');
            chars[position] = isDigit ? keyPressed : ' ';
            if (isBackspace)
                setFocus(position - 1);
            else
                setFocus(position + 1);
            return chars.join('');
        });
    }, [setFocus, length]);
    const handleKeyDown = useCallback((position, event) => handleCodeChange(position, event.key), [handleCodeChange]);
    const handleFocus = useCallback((position) => {
        if (position < code.length) {
            inputsRef.current[position]?.select();
        }
    }, [code]);
    const handlePaste = useCallback((position, event) => {
        const pastedCode = sanitizeDigits(event.clipboardData.getData('text')).slice(0, length);
        const existingCode = sanitizeDigits(code.slice(0, position));
        const newCode = (existingCode + pastedCode).padEnd(length, ' ').slice(0, length);
        setCode(newCode);
    }, [code, length]);
    const handlePasteFromClipboard = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            setCode(sanitizeDigits(text).slice(0, length).padEnd(length, ' '));
        }
        catch {
            throw new Error('Clipboard access denied');
        }
    }, [length]);
    const getValueAt = useCallback((index) => code[index], [code]);
    return {
        code,
        setCode,
        inputsRef,
        getValueAt,
        setFocus,
        handleKeyDown,
        handleFocus,
        handlePaste,
        handlePasteFromClipboard,
    };
}
